<template>
  <div v-if="mostrar" class="overlay">
    <div class="modal-content" @click.stop @mousedown.stop>
      <div class="modal-header">
        <span class="modal-label">Confirmação de Segurança</span>
        <button type="button" class="btn-fechar-modal" @click="$emit('cancelar')">
          ×
        </button>
      </div>

      <div class="modal-body">
        <p>
          {{
            descricao ||
            "Para confirmar esta ação, por favor insira sua senha do SIGAA."
          }}
        </p>

        <!-- Campo de login (exibido apenas quando mostrarLogin=true) -->
        <div v-if="mostrarLogin" class="input-group">
          <label for="login-sigaa">Login SIGAA:</label>
          <input
            id="login-sigaa"
            name="confirmar-login-seguranca"
            type="text"
            v-model="login"
            placeholder="Seu login do SIGAA"
            autocomplete="off"
            ref="inputLogin"
          />
        </div>

        <div class="input-group">
          <label for="senha-sigaa">Senha do SIGAA:</label>
          <input
            id="senha-sigaa"
            name="confirmar-senha-seguranca"
            type="password"
            v-model="senha"
            placeholder="Digite sua senha do SIGAA"
            autocomplete="new-password"
            @keydown.enter.prevent="confirmar"
            ref="inputSenha"
          />
        </div>

        <p v-if="erro" class="msg-erro">
          {{ erro }}
        </p>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn-cancelar" @click="$emit('cancelar')" :disabled="carregando">
          Cancelar
        </button>
        <button type="button" class="btn-confirmar" @click="confirmar" :disabled="!podeConfirmar || carregando">
          {{ carregando ? "Verificando..." : "Confirmar" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useToast } from "@/composables/useToast";
import userInfos from "@/utils/userInfos";

const toast = useToast();

const props = defineProps({
  mostrar: Boolean,
  carregando: Boolean,
  erro: String,
  /** Quando true, exibe também o campo de login SIGAA */
  mostrarLogin: {
    type: Boolean,
    default: false,
  },
  /** Texto descritivo exibido no corpo do modal */
  descricao: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["confirmar", "cancelar"]);

const login = ref("");
const senha = ref("");
const inputLogin = ref(null);
const inputSenha = ref(null);

const podeConfirmar = computed(() => {
  if (props.mostrarLogin) return login.value.trim() && senha.value;
  return !!senha.value;
});

watch(
  () => props.mostrar,
  (novoValor) => {
    if (novoValor) {
      const infos = userInfos.getUserInfos() || {};
      login.value = (infos.loginSigaa || infos.cpf || window.localStorage.getItem("loginSigaa") || "").trim();
      senha.value = "";
      nextTick(() => {
        if (props.mostrarLogin && !login.value && inputLogin.value) {
          inputLogin.value.focus();
        } else if (inputSenha.value) {
          inputSenha.value.focus();
        }
      });
    }
  }
);

watch(
  () => props.erro,
  (novoErro) => {
    if (novoErro) {
      toast.erro(novoErro);
    }
  }
);

function confirmar() {
  if (!podeConfirmar.value) return;

  if (props.mostrarLogin) {
    // Emite objeto { login, senha } quando modo SIGAA está ativo
    emit("confirmar", { login: login.value.trim(), senha: senha.value });
  } else {
    // Mantém compatibilidade: emite apenas a string da senha
    emit("confirmar", senha.value);
  }
}
</script>

<style scoped>
.modal-content {
  background: var(--branco);
  border-radius: 12px;
  width: 100%;
  max-width: 450px;
  padding: 0;
  overflow: hidden;
}

.modal-body {
  padding: 16px 24px;
  background: var(--branco);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}

.modal-body p {
  color: var(--preto);
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.input-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--preto);
  text-align: left;
}

.input-group input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid var(--cinza-medio);
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.input-group input:focus {
  outline: none;
  border-color: var(--roxo-primario);
  box-shadow: 0 0 0 2px hsla(258, 53%, 28%, 0.1);
}

.msg-erro {
  color: var(--vermelho);
  font-size: 0.85rem;
  margin: 0 !important;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: var(--branco);
}

.btn-cancelar {
  padding: 8px 16px;
  background: var(--vermelho-escuro);
  color: var(--branco);
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-cancelar:hover:not(:disabled) {
  background: var(--vermelho);
}

.btn-confirmar {
  padding: 8px 16px;
  border: none;
  background: var(--roxo-primario);
  color: var(--branco);
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-confirmar:hover:not(:disabled) {
  background: var(--roxo-medio);
}

.btn-confirmar:disabled,
.btn-cancelar:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
